import React, { useCallback, useState } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { Dialog } from '@modernloop/shared/components';
import { Stack, TextField } from '@mui/material';
import { useDebouncedCallback } from 'use-debounce';

import { useTaskRequestChangeMutation } from 'src/generated/mloop-graphql';

import { useActivityFeedRefetch } from 'src/hooks/useActivityFeedRefetch';

import ScheduleTaskFlags from 'src/views-new/ScheduleTask/ScheduleTaskDetails/ScheduleTaskFlags';

import { DEBOUNCE_TIMEOUT } from 'src/constants';

type TaskRequestChangeModalProps = {
  taskId: string;
  onClose: () => void;
};

const TaskRequestChangeModal = ({ taskId, onClose }: TaskRequestChangeModalProps) => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [note, setNote] = useState('');
  const [taskRequestChangeMutation] = useTaskRequestChangeMutation();
  const refetchActivityFeed = useActivityFeedRefetch();

  const debouncedSetNote = useDebouncedCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setNote(event?.target?.value?.trim() || '');
  }, DEBOUNCE_TIMEOUT);

  const handleRequestChange = useCallback(() => {
    setIsSubmitting(true);
    // eslint-disable-next-line promise/catch-or-return
    taskRequestChangeMutation({
      variables: {
        input: {
          taskId,
          note,
        },
      },
    }) // eslint-disable-next-line promise/always-return
      .then(() => {
        refetchActivityFeed();
      })
      .finally(() => {
        setIsSubmitting(false);
        onClose();
      });
  }, [note, onClose, refetchActivityFeed, taskId, taskRequestChangeMutation]);

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('hiring-dashboard-tasks:tasks.requestChange.title')}
      subTitle={t('hiring-dashboard-tasks:tasks.requestChange.subtitle')}
      cancelOptions={{ label: t('hiring-dashboard-common:common.cancel'), onClick: onClose }}
      submitOptions={{
        label: t('hiring-dashboard-tasks:tasks.requestChange.submitLabel'),
        onClick: handleRequestChange,
        isDisabled: !note || isSubmitting,
        isLoading: isSubmitting,
      }}
    >
      <Stack spacing={1}>
        <TextField
          multiline
          rows={4}
          autoFocus
          fullWidth
          placeholder={t('hiring-dashboard-tasks:tasks.requestChange.placeholder')}
          onChange={debouncedSetNote}
        />
      </Stack>
    </Dialog>
  );
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const TaskRequestChangeMutation = gql`
  ${ScheduleTaskFlags.fragments.taskFlags}
  mutation TaskRequestChange($input: TaskRequestChangeInput!) {
    taskRequestChange(input: $input) {
      task {
        id
        flags {
          ...ScheduleTaskFlags_taskFlag
        }
      }
    }
  }
`;

export default TaskRequestChangeModal;
